import moment from 'moment-timezone';
import { isValid, parse } from 'date-fns';
import { Action, BirchDatePickerFormat, Decision, StageType } from './types';
import { withLocalePrefix } from '../../shared/utils/translation';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { useCurrentUser } from './hooks/useCurrentUser';

/**
 * Conditionally format date if older than 1 year ago
 * @param {*} date
 * @param {*} format
 * @returns formated date string
 */
export const formatDate = (date?: string | null, format = 'MMM D') => {
  if (!date) {
    return '';
  }
  const formattedDate = moment(date);
  const now = moment();
  if (now.diff(formattedDate, 'years') >= 1) {
    format = 'MMM D, YYYY';
  }
  return formattedDate.format(format);
};

/**
 * Formats date based on current_user date/time prefrerence. Conditionally format date if older than 1 year ago
 * @param date - ISO date string
 * @param includeTime - include time in the format (e.g. May 30 vs. May 30 at 10:00 AM)
 * @returns - formatted date/time string
 */
export const formatDateByUserPreference = (
  date?: string | null,
  includeTime = false
) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const currentUser = useCurrentUser();
  if (!currentUser) {
    throw new Error('No currentUser');
  }
  if (!date) {
    return '';
  }
  let format = currentUser.date_format as string;
  const formattedDate = moment(date).tz(currentUser.time_zone);
  if (includeTime) {
    format = `${format} [at] ${currentUser.time_format ?? 'hh:mm A'}`;
  }
  return formattedDate.format(format);
};

export const rightBarOpenKey = 'candidate_profile/right_bar_open';
export const rightBarTabKey = 'candidate_profile/right_bar_tab';
export const privatizeFieldsKey = 'candidate_profile/privatize_fields';
export const candidateDetailsOpenKey =
  'candidate_profile/right_bar/candidate_details_open';
export const applicationDetailsOpenKey =
  'candidate_profile/right_bar/application_details_open';
export const securityScreeningOpenKey =
  'candidate_profile/right_bar/security_screening_open';

const activityFeedTranslate = withLocalePrefix(
  'candidate_profile_redesign.activity_feed'
);
export const sortByItems = [
  { value: 'desc', label: activityFeedTranslate('most_recent') },
  { value: 'asc', label: activityFeedTranslate('oldest') },
];

type ItemResponse = {
  text: string;
  id?: number;
  children?: ItemResponse[];
};

export const recursiveMapToItems = (
  items: ItemResponse[],
  disableRootOptions = true
): Item[] => {
  return items.map((item) => {
    const option = { label: item.text } as Item;
    if (item.children) {
      option.items = recursiveMapToItems(item.children, disableRootOptions);
      if (disableRootOptions) {
        option.disabled = true;
      }
    }
    option.value = item.id?.toString() ?? '';

    return option;
  });
};

export function isStringAction(
  action?: Action
): action is { name: string; action: string; dataProvides?: string } {
  if (!action) {
    return false;
  }
  return typeof action.action === 'string';
}

export function isFunctionAction(
  action?: Action
): action is { name: string; action: () => void; dataProvides?: string } {
  if (!action) {
    return false;
  }
  return typeof action.action === 'function';
}

/**
 * Create a date object given a date string and the date format
 * @param dateStr - The date string
 * @param format - The date format
 * @returns Date object or undefined if the date string is invalid
 */
export function parseDateString(
  dateStr: string,
  format: BirchDatePickerFormat
) {
  const parsedDate = parse(dateStr, format, new Date());

  if (isValid(parsedDate)) {
    return parsedDate;
  }

  return undefined;
}

/**
 * Given an item with a date range, split the date range into individual days while maintaining the original item data
 * @param item - The original item object
 * @param start - Start time in Moment object
 * @param end - End time in Moment object
 * @returns Array of item objects with startMoment and endMoment properties
 */
export function splitDateRangeIntoDays<T>(
  item: T,
  start: moment.Moment,
  end: moment.Moment
) {
  const days: Array<
    T & {
      startMoment: moment.Moment;
      endMoment: moment.Moment;
    }
  > = [];

  let currentDay = start.clone().startOf('day');
  const endOfCurrentDay = currentDay.clone().endOf('day');

  while (currentDay.isBefore(end)) {
    const dayStart = currentDay.isSame(start, 'day')
      ? start
      : currentDay.clone().startOf('day');
    const dayEnd = currentDay.isSame(end, 'day') ? end : endOfCurrentDay;

    days.push({
      ...item,
      startMoment: dayStart,
      endMoment: dayEnd,
    });

    currentDay = currentDay.add(1, 'day').startOf('day');
  }

  return days;
}

/**
 * Gets the note anchor from the URL
 * @returns - Object with anchorType and taggedNoteId properties
 */
export const getNoteAnchor = () => {
  // Note anchor: #note_[id]
  const anchor = window.location.hash.substring(1);
  const splitAnchor = anchor.split('_');
  const anchorType = splitAnchor[0];
  const taggedNoteId = anchorType === 'note' ? Number(splitAnchor[1]) : null;

  return {
    anchorType,
    taggedNoteId,
  };
};

/**
 * Gets the form anchor from the URL
 * @returns - a form id or null if the param is not provided
 */
export const getFormAnchor = () => {
  // Form anchor: #form_[id]
  const url = new URL(window.location.href);
  return url.searchParams.get('form_id');
};

export function cleanupLocalStorage(key: string) {
  Object.keys(localStorage)
    .filter((x) => x.startsWith(key.toString()))
    .forEach((x) => localStorage.removeItem(x));
}

/**
 * Returns the current stage.
 *
 * If the candidate is hired, return the last stage.
 * If there is no current stage, return the first stage.
 *
 * @param stages - Array of stages
 * @param decision - Application decision
 * @returns - StageType object
 */
export const getCurrentStage = (
  stages: StageType[],
  decision: Decision | null
) => {
  const isHired = decision?.status === 'hired';
  return isHired
    ? stages[stages.length - 1]
    : stages.find((stage) => stage.is_current_stage) ?? stages[0];
};

type ItemWithUpdatedAt = {
  updated_at: string;
};

/**
 * Checks if an item is unread based on the last seen timestamp and the most recent item
 * @param lastSeenTimestamp - The last seen timestamp
 * @param mostRecentItem - The most recent item
 * @returns - true if the item is unread, false otherwise
 */
export const isItemUnread = (
  lastSeenTimestamp: string | null | undefined,
  mostRecentItem: ItemWithUpdatedAt | null | undefined
): boolean => {
  if (!mostRecentItem?.updated_at) {
    return false;
  }

  if (!lastSeenTimestamp) {
    return true;
  }

  const lastSeenDate = new Date(lastSeenTimestamp);
  const mostRecentDate = new Date(mostRecentItem.updated_at);

  return lastSeenDate < mostRecentDate;
};

/**
 * Checks if a display file name has a file extension.
 * Mainly used checking if a document name being displayed is generic or not (e.g. "Resume", "Cover Letter")
 *
 * @param name - File name to check
 * @returns - true if the file name has a file extension, false otherwise
 */
export const hasFileExtension = (name: string) => {
  return /\.[a-zA-Z0-9]+$/.test(name);
};

/**
 * Wraps a name or other PII string in `<span class="pii-blur">` for i18n copy
 * rendered with dangerouslySetInnerHTML.
 */
export const wrapInPiiBlurSpan = (text: string): string =>
  `<span class="pii-blur">${text}</span>`;
